// src/services/projectDefinitionsApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// API Response Types
export interface ProjectDefinitionListItem {
  projectDefinitionId: number;
  name: string;
  description: string | null;
  projectId: number | null;
  createdAt: string;
  lastUpdated: string;
  createdBy: number;
}

export interface ProjectDefinitionsListResponse {
  projectDefinitions: ProjectDefinitionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PhaseDefinitionResponse {
  phaseDefinitionId: number;
  projectDefinitionId: number;
  name: string;
  description: string | null;
  entryCriteria: string | null;
  displayOrder: number;
  transitions: TransitionDefinitionResponse[];
  exitActions: ExitActionResponse[];
}

export interface TransitionDefinitionResponse {
  transitionId: number;
  fromPhaseDefinitionId: number;
  toPhaseDefinitionId: number;
  condition: string | null;
}

export interface ExitActionResponse {
  exitActionId: number;
  phaseDefinitionId: number;
  exitAction: string;
  target: string | null;
  displayOrder: number;
}

export interface ProjectDefinitionResponse {
  projectDefinitionId: number;
  name: string;
  description: string | null;
  projectId: number | null;
  createdAt: string;
  lastUpdated: string;
  createdBy: number;
  phases: PhaseDefinitionResponse[];
}

// API Request Types
export interface CreatePhaseRequest {
  name: string;
  description: string | null;
  entryCriteria: string | null;
  displayOrder: number;
  transitions: Array<{
    toPhaseDefinitionId: number;
    condition: string;
  }>;
  exitActions: Array<{
    exitAction: string;
    target: string | null;
    displayOrder: number;
  }>;
}

export interface CreateProjectDefinitionRequest {
  name: string;
  description: string | null;
  projectId: number | null;
  phases: CreatePhaseRequest[];
}

export type UpdateProjectDefinitionRequest = CreateProjectDefinitionRequest;

// Error Type
export interface ProjectDefinitionApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// List project definitions
export async function listProjectDefinitions(params?: {
  page?: number;
  pageSize?: number;
  projectId?: number;
}): Promise<ProjectDefinitionsListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.projectId) queryParams.append('projectId', params.projectId.toString());

  const url = `${API_BASE_URL}/api/project-definitions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectDefinitionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to list project definitions: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectDefinitionApiError;
  }

  return response.json();
}

// Get project definition by ID
export async function getProjectDefinition(projectDefinitionId: number): Promise<ProjectDefinitionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/project-definitions/${projectDefinitionId}`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectDefinitionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project definition not found', code: 'NOT_FOUND' } as ProjectDefinitionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to get project definition: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectDefinitionApiError;
  }

  return response.json();
}

// Create project definition
export async function createProjectDefinition(
  data: CreateProjectDefinitionRequest
): Promise<ProjectDefinitionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/project-definitions`, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectDefinitionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectDefinitionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to create project definition: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectDefinitionApiError;
  }

  return response.json();
}

// Update project definition
export async function updateProjectDefinition(
  projectDefinitionId: number,
  data: UpdateProjectDefinitionRequest
): Promise<ProjectDefinitionResponse> {
  const response = await fetch(`${API_BASE_URL}/api/project-definitions/${projectDefinitionId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectDefinitionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project definition not found', code: 'NOT_FOUND' } as ProjectDefinitionApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as ProjectDefinitionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update project definition: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectDefinitionApiError;
  }

  return response.json();
}

// Delete project definition
export async function deleteProjectDefinition(projectDefinitionId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/project-definitions/${projectDefinitionId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as ProjectDefinitionApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Project definition not found', code: 'NOT_FOUND' } as ProjectDefinitionApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete project definition: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as ProjectDefinitionApiError;
  }
}

